import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import PasswordService from '@service/PasswordService';
import { useToastContext } from '@contexts/ToastContext';
import { useSettingsStore } from '@hooks/useStores';
import { EyeIcon, EyeOffIcon, CheckIcon, LockIcon, SunIcon, MoonIcon } from '@components/common/ui/icons';
import { Loader } from '@components/common/ui';
import * as styles from '@pages/ResetPasswordPage.module.css';

export const ResetPasswordPage: React.FC = observer(function ResetPasswordPage() {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const toast = useToastContext();
    const settingsStore = useSettingsStore();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [validating, setValidating] = useState(true);
    const [isValidToken, setIsValidToken] = useState(false);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        password: '',
        confirmPassword: '',
    });

    // Валидация токена при загрузке страницы
    useEffect(() => {
        let isMounted = true;

        const validateToken = async () => {
            if (!token) {
                if (isMounted) {
                    setValidating(false);
                    setIsValidToken(false);
                }
                return;
            }

            try {
                await PasswordService.validateResetToken(token);
                if (isMounted) {
                    setIsValidToken(true);
                }
            } catch (error: any) {
                if (isMounted) {
                    const errorMessage =
                        error?.response?.data?.message || t('resetPassword.invalidTokenMessage');
                    toast.error(errorMessage);
                    setIsValidToken(false);
                }
            } finally {
                if (isMounted) {
                    setValidating(false);
                }
            }
        };

        validateToken();

        return () => {
            isMounted = false;
        };
    }, [token]); // Убрал toast из зависимостей

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.password || !formData.confirmPassword) {
            toast.warning(t('auth.fillAllFields'));
            return;
        }

        if (formData.password !== formData.confirmPassword) {
            toast.error(t('auth.passwordMismatch'));
            return;
        }

        if (formData.password.length < 3) {
            toast.error(t('auth.minPassword'));
            return;
        }

        if (!token) {
            toast.error(t('resetPassword.tokenNotFound'));
            return;
        }

        setLoading(true);
        try {
            await PasswordService.resetPassword(token, formData.password);
            setSuccess(true);
            toast.success(t('resetPassword.success'));

            // Перенаправляем на страницу входа через 2 секунды
            setTimeout(() => {
                navigate('/');
            }, 2000);
        } catch (error: any) {
            const errorMessage = error?.response?.data?.message || t('resetPassword.requestFailed');
            toast.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const themeBtn = (
        <button
            className={styles.themeToggle}
            onClick={() => settingsStore.toggleTheme()}
            title={settingsStore.theme === 'light' ? t('auth.switchThemeDark') : t('auth.switchThemeLight')}
        >
            {settingsStore.theme === 'light'
                ? <MoonIcon className={styles.themeToggleIcon} />
                : <SunIcon className={styles.themeToggleIcon} />
            }
        </button>
    );

    if (validating) {
        return (
            <div className={styles.container}>
                {themeBtn}
                <div className={styles.card}>
                    <Loader variant="spinner" size="lg" text={t('resetPassword.validatingToken')} />
                </div>
            </div>
        );
    }

    if (!isValidToken) {
        return (
            <div className={styles.container}>
                {themeBtn}
                <div className={styles.card}>
                    <div className={styles.errorState}>
                        <div className={styles.errorIcon}>✕</div>
                        <h1 className={styles.errorTitle}>{t('resetPassword.invalidTokenTitle')}</h1>
                        <p className={styles.errorText}>{t('resetPassword.invalidTokenText')}</p>
                        <button onClick={() => navigate('/')} className={styles.backButton}>
                            {t('resetPassword.backHome')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className={styles.container}>
                {themeBtn}
                <div className={styles.card}>
                    <div className={styles.successState}>
                        <div className={styles.successIcon}>
                            <CheckIcon className={styles.checkIcon} />
                        </div>
                        <h1 className={styles.successTitle}>{t('resetPassword.successTitle')}</h1>
                        <p className={styles.successText}>{t('resetPassword.successText')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            {themeBtn}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <div className={styles.iconWrapper}>
                        <LockIcon className={styles.icon} />
                    </div>
                    <h1 className={styles.title}>{t('resetPassword.title')}</h1>
                    <p className={styles.description}>{t('resetPassword.description')}</p>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className={styles.cardContent}>
                        <div className={styles.field}>
                            <label htmlFor="resetPassword" className={styles.label}>
                                {t('resetPassword.newPassword')}
                            </label>
                            <div className={styles.passwordWrapper}>
                                <input
                                    id="resetPassword"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={handleChange('password')}
                                    required
                                    className={styles.input}
                                    disabled={loading}
                                    autoFocus
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className={styles.passwordToggle}
                                    disabled={loading}
                                >
                                    {showPassword ? (
                                        <EyeOffIcon className={styles.eyeIcon} />
                                    ) : (
                                        <EyeIcon className={styles.eyeIcon} />
                                    )}
                                </button>
                            </div>
                        </div>

                        <div className={styles.field}>
                            <label htmlFor="resetConfirmPassword" className={styles.label}>
                                {t('resetPassword.confirmPassword')}
                            </label>
                            <div className={styles.passwordWrapper}>
                                <input
                                    id="resetConfirmPassword"
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={formData.confirmPassword}
                                    onChange={handleChange('confirmPassword')}
                                    required
                                    className={styles.input}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                    className={styles.passwordToggle}
                                    disabled={loading}
                                >
                                    {showConfirmPassword ? (
                                        <EyeOffIcon className={styles.eyeIcon} />
                                    ) : (
                                        <EyeIcon className={styles.eyeIcon} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={styles.cardFooter}>
                        <button type="submit" className={styles.submitButton} disabled={loading}>
                            {loading ? t('resetPassword.submitting') : t('resetPassword.submit')}
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate('/')}
                            className={styles.cancelButton}
                            disabled={loading}
                        >
                            {t('resetPassword.cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});
